'use client'

import { useState } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { toast } from '@/lib/toast'
import { navigateUrl } from '@/utils/client-request'
import { useAuth } from './useAuth'
import { useCart } from './useCart'
import type { useCustomFields } from './useCustomFields'
import type { useAddress } from './useAddress'
import type { usePayment } from './usePayment'
import type { useDelivery } from './useDelivery'
import type { useCustomerDetails } from './useCustomerDetails'

type CustomFields = ReturnType<typeof useCustomFields>
type Address = ReturnType<typeof useAddress>
type Payment = ReturnType<typeof usePayment>
type Delivery = ReturnType<typeof useDelivery>
type CustomerDetails = ReturnType<typeof useCustomerDetails>

type Args = {
  vendor: any
  subscription: any
  customerDetails: CustomerDetails
  customFields: CustomFields
  address: Address
  payment: Payment
  delivery: Delivery
  totalPrice: number
  // What checkout actually charges today: the deposit on a split payment, else the total.
  payNowAmount?: number
  isFreeOrder: boolean
  hasDigitalProduct: boolean
  hasDeliveryOption: boolean
  showDeliverySection: boolean
}

export function useCheckoutSubmit(args: Args) {
  const { vendor, subscription, customerDetails, customFields, address, payment, delivery, totalPrice, payNowAmount, isFreeOrder, hasDigitalProduct, hasDeliveryOption, showDeliverySection } = args
  const { session, isGuest } = useAuth()
  const { cart, canGuestOrder, checkout, appliedGiftCard, giftCardDiscount, appliedLoyaltyVoucher } = useCart()
  const router = useRouter()
  const pathname = usePathname()
  const products = cart?.products || []
  // Stays true from the moment we hit /order until the order page commits and this
  // tree unmounts — the cart is emptied inside checkout() before it returns, so the
  // checkout templates need this to know not to fall back to their empty-cart state.
  const [isPlacingOrder, setIsPlacingOrder] = useState(false)

  const submit = async () => {
    if (isPlacingOrder) return
    if (isGuest && !canGuestOrder) {
      toast.error('Та захиалга өгөхийн тулд нэвтэрсэн байх шаардлагатай')
      router.push(navigateUrl(`/login?redirect=${pathname}`, pathname))
      return
    }

    const customerValidation = customerDetails.validate({ hasDigitalProduct })
    if (!customerValidation.ok) return toast.error(customerValidation.error)

    const customFieldsValidation = customFields.validateRequired(products, vendor, subscription)
    if (!customFieldsValidation.ok) return toast.error(customFieldsValidation.error)

    const paymentValidation = payment.validatePayment({ totalPrice, isFreeOrder, payNowAmount })
    if (!paymentValidation.ok) return toast.error(paymentValidation.error)

    if (showDeliverySection) {
      const deliveryValidation = delivery.validateDelivery()
      if (!deliveryValidation.ok) return toast.error(deliveryValidation.error)
      if (delivery.selectedDelivery === 'delivery' && hasDeliveryOption) {
        const addressValidation = address.validateForCheckout()
        if (!addressValidation.ok) return toast.error(addressValidation.error)
        if (!session?.user?.id) address.setAddressObj(address.composeAddressObj())
      }
    }

    const checkoutAddressObj = (delivery.selectedDelivery === 'delivery' && hasDeliveryOption) ? address.getCheckoutAddressObj() : null
    const { productCustomFields, orderCustomFields } = customFields.buildPayload(products, vendor)
    const deliveryType = showDeliverySection ? delivery.selectedDelivery : 'none'
    const customerName = customerDetails.getCustomerName()

    try {
      setIsPlacingOrder(true)
      const data = await checkout({
        fullName: customerName,
        email: customerDetails.email,
        phone: customerDetails.phone,
        additionalPhone: customerDetails.additionalPhone,
        cartId: cart?.id,
        selectedDelivery: deliveryType,
        selectedPayment: payment.selectedPayment,
        addressObj: checkoutAddressObj,
        branchId: delivery.selectedDelivery === 'pickup' ? delivery.selectedBranch : undefined,
        products,
        giftCardId: appliedGiftCard?.id || null,
        giftCardAmount: giftCardDiscount || 0,
        giftCardCode: appliedGiftCard?.code || null,
        loyaltyVoucherId: appliedLoyaltyVoucher?.id || null,
        loyaltyVoucherCode: appliedLoyaltyVoucher?.code || null,
        storepayPhone: payment.selectedPayment === 'storepay' ? payment.storepayPhone : undefined,
        lendPhone: payment.selectedPayment === 'lend' ? payment.lendPhone : undefined,
        productCustomFields,
        orderCustomFields,
      })
      if (!data) { setIsPlacingOrder(false); return toast.error('Захиалга үүсгэхэд алдаа гарлаа') }
      if (data.error) { setIsPlacingOrder(false); return toast.error(data.error) }
      if (!data.customId) { setIsPlacingOrder(false); return toast.error('Захиалга үүсгэхэд алдаа гарлаа') }
      if (session?.user?.id) {
        router.push(navigateUrl(`/order/${data.customId}`, pathname))
      } else {
        router.push(navigateUrl(`/order/${data.customId}/${customerDetails.phone}`, pathname))
      }
    } catch (error) {
      setIsPlacingOrder(false)
      console.error('Error during checkout:', error)
      toast.error('Захиалга үүсгэхэд алдаа гарлаа')
    }
  }

  return { submit, isPlacingOrder }
}
